
import { HotmartRum } from '@commons/observability'


const DEVICE_FINGERPRINT_ELEMENT = 'did-customField'


/**
 * @description Broprint.js - A lightweight browser fingerprinting library.
 * @author Rajesh Royal
 * @url https://github.com/Rajesh-Royal/Broprint.js
*/
// @ts-ignore
"use strict";var Broprint=(()=>{var g=Object.defineProperty;var T=Object.getOwnPropertyDescriptor;var y=Object.getOwnPropertyNames;var B=Object.prototype.hasOwnProperty;var F=(t,f)=>{for(var e in f)g(t,e,{get:f[e],enumerable:!0})},M=(t,f,e,r)=>{if(f&&typeof f=="object"||typeof f=="function")for(let n of y(f))!B.call(t,n)&&n!==e&&g(t,n,{get:()=>f[n],enumerable:!(r=T(f,n))||r.enumerable});return t};var D=t=>M(g({},"__esModule",{value:!0}),t);var p=(t,f,e)=>new Promise((r,n)=>{var a=i=>{try{s(e.next(i))}catch(u){n(u)}},d=i=>{try{s(e.throw(i))}catch(u){n(u)}},s=i=>i.done?r(i.value):Promise.resolve(i.value).then(a,d);s((e=e.apply(t,f)).next())});var O={};F(O,{getDeviceFingerprint:()=>b});var m=function(t,f=0){let e=3735928559^f,r=1103547991^f;for(let n=0,a;n<t.length;n++)a=t.charCodeAt(n),e=Math.imul(e^a,2654435761),r=Math.imul(r^a,1597334677);return e=Math.imul(e^e>>>16,2246822507)^Math.imul(r^r>>>13,3266489909),r=Math.imul(r^r>>>16,2246822507)^Math.imul(e^e>>>13,3266489909),4294967296*(2097151&r)+(e>>>0)};var S=()=>{var t=document.createElement("canvas");return!!(t.getContext&&t.getContext("2d"))},h=()=>{if(!S())return"broprint.js";var t=document.createElement("canvas"),f=t.getContext("2d"),e="BroPrint.65@345876";return f.textBaseline="top",f.font="14px 'Arial'",f.textBaseline="alphabetic",f.fillStyle="#f60",f.fillRect(125,1,62,20),f.fillStyle="#069",f.fillText(e,2,15),f.fillStyle="rgba(102, 204, 0, 0.7)",f.fillText(e,4,17),t.toDataURL()};var v=function(){var t=null,f=null,e=null,r=null,n=null,a=null;function d(o,l=!1){a=o;try{s(),e.connect(r),r.connect(t.destination),e.start(0),t.startRendering(),t.oncomplete=C}catch(x){if(l)throw x}}function s(){i(),f=t.currentTime,u(),w()}function i(){var o=window.OfflineAudioContext||window.webkitOfflineAudioContext;t=new o(1,44100,44100)}function u(){e=t.createOscillator(),e.type="triangle",e.frequency.setValueAtTime(1e4,f)}function w(){r=t.createDynamicsCompressor(),c("threshold",-50),c("knee",40),c("ratio",12),c("reduction",-20),c("attack",0),c("release",.25)}function c(o,l){r[o]!==void 0&&typeof r[o]=="function"&&r[o].setValueAtTime(l,t.currentTime)}function C(o){A(o),r.disconnect()}function A(o){for(var l=null,x=4500;5e3>x;x++){var P=o.renderedBuffer.getChannelData(0)[x];l+=Math.abs(P)}if(n=l.toString(),typeof a=="function")return a(n)}return{run:d}}();var b=()=>{let t=new Promise((e,r)=>{v.run(function(n){e(n)})});return new Promise((e,r)=>{t.then(n=>p(null,null,function*(){let a="";navigator.brave&&(yield navigator.brave.isBrave())?a=window.btoa(n)+h():a=window.btoa(n)+h(),e(m(a,0))})).catch(()=>{try{e(m(h()).toString())}catch(n){r("Failed to generate the finger print of this browser")}})})};try{typeof window!="undefined"&&!window.getDeviceFingerprint&&(window.getDeviceFingerprint=b)}catch(t){}return D(O);})();

declare global {
  interface Window {
    DEVICE_FINGERPRINT?: string | undefined;
    RETRIEVE_DEVICE_FINGERPRINT?: boolean;
    getDeviceFingerprint?: (...args: any[]) => Promise<any>;
  }
}

class FingerprintError extends Error {
  constructor(message?: string) {
    super(message)
    this.name = 'FingerprintError'
  }
}

/**
* @description Generates and caches a device fingerprint for the current browser session.
* @returns {Promise<string | undefined>} A promise that resolves to the device fingerprint
*   string (cached or newly generated), or undefined if a fingerprint could not be
*   determined.
*/
const generateDeviceFingerprint = async (): Promise<string | undefined> => {
  // check if the feature toggle is enabled
  if (!window.RETRIEVE_DEVICE_FINGERPRINT) return

  // return cached fingerprint if available
  if (window.DEVICE_FINGERPRINT) return window.DEVICE_FINGERPRINT
  
  try {
    if (typeof window.getDeviceFingerprint !== 'function') {
      throw new FingerprintError('getDeviceFingerprint is not available')
    }
    
    const fingerprint = await window.getDeviceFingerprint()
    window.DEVICE_FINGERPRINT = fingerprint
    
    console.log('Device Fingerprint:', fingerprint)
  } catch (error) {
    HotmartRum.captureException(error)
    console.error('Error generating device fingerprint:', error)
  }
  
  return window.DEVICE_FINGERPRINT
}


/**
 * Attaches the device fingerprint to the form input field.
 *
 * @param fingerprint - Device fingerprint string.
 */
const attachDeviceFingerprintToForm = (fingerprint): void => {
  // check if the feature toggle is enabled
  if (!window.RETRIEVE_DEVICE_FINGERPRINT) return

  try {
    if (!fingerprint) {
      throw new FingerprintError('Device fingerprint is undefined')
    }

    const input = document.getElementById(DEVICE_FINGERPRINT_ELEMENT) as HTMLInputElement | null
    
    if (!input) {
      throw new FingerprintError('Input field for device fingerprint not found')
    }
  
    input.value = fingerprint
  } catch (error) {
    HotmartRum.captureException(error)
    console.error('Error attaching device fingerprint to form:', error)
  }
}


/**
 * Set device fingerprint to user.
 *
 * @param fingerprint - Device fingerprint string.
 */
const setDeviceFingerprintToUser = (fingerprint): void => {
  // check if the feature toggle is enabled
  if (!window.RETRIEVE_DEVICE_FINGERPRINT) return

  HotmartRum.setTag('deviceFingerprint', fingerprint)
}


export { attachDeviceFingerprintToForm, generateDeviceFingerprint, setDeviceFingerprintToUser }